import {
  ApartmentOutlined,
  ArrowRightOutlined,
  BulbOutlined,
  CompassOutlined,
  DownloadOutlined,
  FireOutlined,
  HomeOutlined,
  LinkOutlined,
  MailOutlined,
  PictureOutlined,
  ReadOutlined,
  RocketOutlined,
  ShareAltOutlined,
  SunOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { Alert, Button, Card, Input, Modal, Spin, Tabs, message } from 'antd';
import {
  useCreateMyLifeReviewShareMutation,
  useDeleteMyLifeReviewShareMutation,
  useGetMyLifeReviewSharesQuery,
  useResendMyLifeReviewShareMutation
} from 'api/LifeReviewApi';
import { UserAccessLevelPlanEnum } from 'api/UsersApi';
import ContainerComponent from 'components/Tool/ContainerComponent/ContainerComponent';
import Footer from 'pages/Client/Footer/Footer';
import Header from 'pages/Client/Header/Header';
import { getFirstIncompletePromptId, useLifeReviewSessionState } from 'pages/LifeReview/useLifeReviewSessionState';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAuthToken, selectAuthUser } from 'store/authSlice';
import { buildApiDownloadUrl, downloadFileFromApi } from 'utils/downloadFile';
import { userHasAccess } from 'utils/utils';
import './LifeReviewPage.css';

const DEFAULT_SECTION_DESCRIPTION = 'Reflect on this chapter of your story at your own pace.';

const SECTION_FALLBACK_COPY: Record<string, string> = {
  childhood: 'Memories from your early years and the people who shaped you.',
  family: 'Important relationships, traditions, and moments with loved ones.',
  career: 'Work experiences, lessons learned, and milestones achieved.',
  values: 'Beliefs, principles, and perspectives that guided your life.',
  legacy: 'Messages, wisdom, and hopes you want to leave behind.',
  'legacy-messages': 'Messages, reflections, and hopes you want to leave behind for the people you love.',
  'military-service-veterans': 'Stories of service, sacrifice, and the memories that stayed with you after military life.',
  relationships: 'Stories about friendships, love, and meaningful connections.',
  milestones: 'Defining moments, achievements, and turning points in your life.',
  home: 'Places you lived and the memories connected to them.'
};

const resolveSectionIcon = (value: string) => {
  if (value.includes('early') || value.includes('child')) {
    return RocketOutlined;
  }
  if (value.includes('family') || value.includes('relationship')) {
    return ApartmentOutlined;
  }
  if (value.includes('love') || value.includes('partnership')) {
    return LinkOutlined;
  }
  if (value.includes('career') || value.includes('purpose') || value.includes('work')) {
    return CompassOutlined;
  }
  if (value.includes('hardship') || value.includes('resilience')) {
    return TrophyOutlined;
  }
  if (value.includes('joy') || value.includes('humor') || value.includes('personality')) {
    return SunOutlined;
  }
  if (value.includes('lesson') || value.includes('advice')) {
    return BulbOutlined;
  }
  if (value.includes('faith') || value.includes('meaning') || value.includes('perspective')) {
    return FireOutlined;
  }
  if (value.includes('moment') || value.includes('history')) {
    return PictureOutlined;
  }
  if (value.includes('legacy') || value.includes('message')) {
    return MailOutlined;
  }
  if (value.includes('military') || value.includes('veteran') || value.includes('service')) {
    return TrophyOutlined;
  }
  if (value.includes('home') || value.includes('place')) {
    return HomeOutlined;
  }

  return ReadOutlined;
};

const normalizeSectionKey = (value?: string) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_&/]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const getSectionDescription = (key: string, description?: string) => {
  const normalizedKey = normalizeSectionKey(key);
  return description || SECTION_FALLBACK_COPY[normalizedKey] || DEFAULT_SECTION_DESCRIPTION;
};

export function LifeReviewPage() {
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const user = useSelector(selectAuthUser);
  const accessToken = useSelector(selectAuthToken);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareRecipientName, setShareRecipientName] = useState('');
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');
  const [hasShareFormSubmitAttempt, setHasShareFormSubmitAttempt] = useState(false);
  const [shareSubmitFeedback, setShareSubmitFeedback] = useState('');
  const [activeInviteFeedback, setActiveInviteFeedback] = useState('');
  const [activeInviteActionError, setActiveInviteActionError] = useState(false);
  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [createLifeReviewShare, { isLoading: isCreatingShare, isError: isCreateShareError }] = useCreateMyLifeReviewShareMutation();
  const [resendLifeReviewShare, { isLoading: isResendingShare }] = useResendMyLifeReviewShareMutation();
  const [deleteLifeReviewShare, { isLoading: isDeletingShare }] = useDeleteMyLifeReviewShareMutation();
  const {
    data: lifeReviewShares = [],
    isFetching: isFetchingLifeReviewShares,
    refetch: refetchLifeReviewShares
  } = useGetMyLifeReviewSharesQuery(undefined, { skip: !isShareModalOpen });
  const { sections, persistedCompletedByPromptId, isLoading, totalPromptCount, completedPromptCount } = useLifeReviewSessionState();
  const hasLifeReviewPaidAccess = Boolean(userHasAccess(user, UserAccessLevelPlanEnum.Premium));

  const trimmedShareRecipientName = shareRecipientName.trim();
  const trimmedShareRecipientEmail = shareRecipientEmail.trim();
  const isShareRecipientNameValid = Boolean(trimmedShareRecipientName);
  const isShareRecipientEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedShareRecipientEmail);
  const showShareRecipientNameError = hasShareFormSubmitAttempt && !isShareRecipientNameValid;
  const showShareRecipientEmailError = hasShareFormSubmitAttempt && !isShareRecipientEmailValid;
  const activeLifeReviewShares = useMemo(
    () => lifeReviewShares.filter((share) => share.status === 'active' || share.status === 'opened'),
    [lifeReviewShares]
  );
  const isActiveInviteActionInProgress = isResendingShare || isDeletingShare;

  const resetShareFormState = () => {
    setShareRecipientName('');
    setShareRecipientEmail('');
    setHasShareFormSubmitAttempt(false);
    setShareSubmitFeedback('');
    setActiveInviteFeedback('');
    setActiveInviteActionError(false);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    resetShareFormState();
  };

  const handleSendShareInvite = async () => {
    if (!isShareRecipientNameValid || !isShareRecipientEmailValid || isCreatingShare) {
      setHasShareFormSubmitAttempt(true);
      return;
    }

    try {
      await createLifeReviewShare({
        recipientName: trimmedShareRecipientName,
        recipientEmail: trimmedShareRecipientEmail
      }).unwrap();
      setShareSubmitFeedback('');
      void refetchLifeReviewShares();
      window.setTimeout(() => {
        closeShareModal();
        void messageApi.success('Invite sent successfully. Please ask them to check their spam or junk folder if needed.');
      }, 600);
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Unable to send invite right now. Please try again.';
      setShareSubmitFeedback(message);
    }
  };

  const handleResendShareInvite = async (shareId: string) => {
    if (isActiveInviteActionInProgress) {
      return;
    }

    try {
      await resendLifeReviewShare({ shareId }).unwrap();
      setActiveInviteActionError(false);
      setActiveInviteFeedback('Invite resent successfully. Please ask them to check their spam or junk folder if needed.');
      await refetchLifeReviewShares();
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Unable to resend invite right now. Please try again.';
      setActiveInviteActionError(true);
      setActiveInviteFeedback(message);
    }
  };

  const handleDeleteShareInvite = async (shareId: string) => {
    if (isActiveInviteActionInProgress) {
      return;
    }

    try {
      await deleteLifeReviewShare({ shareId }).unwrap();
      setActiveInviteActionError(false);
      setActiveInviteFeedback('Shared link deleted.');
      await refetchLifeReviewShares();
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Unable to delete this shared link right now. Please try again.';
      setActiveInviteActionError(true);
      setActiveInviteFeedback(message);
    }
  };

  const hasPrompts = Boolean(totalPromptCount);
  const continuePromptId = getFirstIncompletePromptId(sections, persistedCompletedByPromptId);
  const completionPercent = totalPromptCount ? Math.round((completedPromptCount / totalPromptCount) * 100) : 0;

  const sectionCards = useMemo(
    () =>
      (sections || []).map((section, index) => {
        const sectionKey = normalizeSectionKey(section.key || section.sectionKey || section.title || `section-${index + 1}`);
        const sectionTitle = section.title || section.key || section.sectionKey || `Life Area ${index + 1}`;
        const prompts = section.prompts || [];
        const sectionTotal = prompts.length;
        const sectionCompleted = prompts.reduce((count, prompt) => count + (persistedCompletedByPromptId[prompt.id] ? 1 : 0), 0);
        const sectionPercent = sectionTotal ? Math.round((sectionCompleted / sectionTotal) * 100) : 0;
        const Icon = resolveSectionIcon(`${sectionTitle} ${sectionKey}`.toLowerCase());

        return {
          key: sectionKey,
          title: sectionTitle,
          description: getSectionDescription(sectionKey, section.description),
          progressText: `${sectionCompleted} of ${sectionTotal} answered`,
          percent: sectionPercent,
          firstPromptId: prompts[0]?.id,
          icon: Icon
        };
      }),
    [persistedCompletedByPromptId, sections]
  );

  const goToSession = () => {
    if (!continuePromptId) {
      navigate('/life-review/session');
      return;
    }

    navigate(`/life-review/session?promptId=${encodeURIComponent(continuePromptId)}`);
  };

  const handleDownloadLifeStory = async () => {
    if (!hasLifeReviewPaidAccess || isDownloadInProgress) {
      if (!hasLifeReviewPaidAccess) {
        navigate('/pricing');
      }

      return;
    }

    setDownloadError('');
    setIsDownloadInProgress(true);

    const downloadUrls = [
      buildApiDownloadUrl('/users/me/life-review/download'),
      buildApiDownloadUrl('/users/me/life-story/download')
    ];

    try {
      for (let index = 0; index < downloadUrls.length; index += 1) {
        const url = downloadUrls[index];

        try {
          await downloadFileFromApi({
            url,
            fallbackFileName: 'Life-Story-Keepsake.zip',
            accessToken
          });
          return;
        } catch (error) {
          const status = Number((error as { status?: number } | undefined)?.status);
          const isLastAttempt = index === downloadUrls.length - 1;

          if (status !== 404 || isLastAttempt) {
            setDownloadError('We couldn’t prepare the download. Please try again.');
            return;
          }
        }
      }
    } finally {
      setIsDownloadInProgress(false);
    }
  };

  return (
    <div className="life-review-page layout-full-height">
      {contextHolder}
      <Header />

      <ContainerComponent className="layout-main-content">
        {isLoading ? (
          <div className="life-review-state life-review-loading">
            <Spin size="large" />
          </div>
        ) : (
          <div className="life-review-state">
            <div className="life-story-hub-header">
              <div className="life-story-hub-header-copy">
                <h1>Life Story</h1>
                <p>Reflect on different parts of your life. Answer at your own pace.</p>
              </div>
              <div className="life-story-hub-actions">
                <button
                  type="button"
                  className="life-story-hub-share-button"
                  aria-label="Download Life Story"
                  onClick={() => void handleDownloadLifeStory()}
                  disabled={isDownloadInProgress}
                >
                  <DownloadOutlined aria-hidden="true" />
                  <span>{isDownloadInProgress ? 'Preparing download...' : 'Download'}</span>
                </button>
                <button
                  type="button"
                  className="life-story-hub-share-button"
                  aria-label="Share Life Story"
                  onClick={() => {
                    if (!hasLifeReviewPaidAccess) {
                      navigate('/pricing');
                      return;
                    }

                    setIsShareModalOpen(true);
                  }}
                >
                  <ShareAltOutlined aria-hidden="true" />
                  <span>Share</span>
                </button>
              </div>
            </div>

            {downloadError && <Alert type="error" showIcon className="life-review-share-feedback" message={downloadError} />}

            {!hasLifeReviewPaidAccess && (
              <Alert
                type="info"
                showIcon
                message="Answering and sharing your Life Review is included with Premium and Unlimited plans."
                action={
                  <Button size="small" type="primary" onClick={() => navigate('/pricing')}>
                    Upgrade Plan
                  </Button>
                }
              />
            )}

            <Card className="life-story-overall-progress-card">
              <div className="life-story-overall-progress-head">
                <h2>Overall progress</h2>
                <p>
                  {completedPromptCount} of {totalPromptCount} questions answered
                </p>
              </div>

              <div className="life-story-overall-progress-row">
                <div className="life-story-overall-progress-track" aria-hidden="true">
                  <div className="life-story-overall-progress-fill" style={{ width: `${completionPercent}%` }} />
                </div>
                <span>{completionPercent}%</span>
              </div>

              <div
                className="life-story-overall-progress-continue"
                role="button"
                tabIndex={0}
                onClick={goToSession}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    goToSession();
                  }
                }}
              >
                <span>Continue where I left off</span>
                <ArrowRightOutlined aria-hidden="true" />
              </div>

              {!hasPrompts && <p className="life-review-help-text">No prompts are available yet. Please try again soon.</p>}
            </Card>

            <h2 className="life-story-areas-title">Life Areas</h2>

            <div className="life-story-areas-list">
              {sectionCards.map((section) => {
                const Icon = section.icon;

                return (
                  <div
                    key={section.key}
                    className="life-story-area-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => section.firstPromptId && navigate(`/life-review/session?promptId=${encodeURIComponent(section.firstPromptId)}`)}
                    onKeyDown={(event) => {
                      if ((event.key === 'Enter' || event.key === ' ') && section.firstPromptId) {
                        event.preventDefault();
                        navigate(`/life-review/session?promptId=${encodeURIComponent(section.firstPromptId)}`);
                      }
                    }}
                  >
                    <div className="life-story-area-icon" aria-hidden="true">
                      <Icon />
                    </div>

                    <div className="life-story-area-content">
                      <h3>{section.title}</h3>
                      <p>{section.description}</p>
                      <span>{section.progressText}</span>
                      <div className="life-story-area-progress-track" aria-hidden="true">
                        <div className="life-story-area-progress-fill" style={{ width: `${section.percent}%` }} />
                      </div>
                    </div>

                    <ArrowRightOutlined className="life-story-area-arrow" aria-hidden="true" />
                  </div>
                );
              })}
            </div>

            <Modal
              open={isShareModalOpen}
              onCancel={closeShareModal}
              footer={null}
              centered
              width={520}
              className="life-review-share-modal"
              title={null}
            >
              <div className="life-review-share-modal-body">
                <h3>Share Life Story</h3>
                <p className="life-review-share-modal-subheader">Send a secure viewing link that gives immediate access to your Life Story.</p>
                <Tabs
                  defaultActiveKey="new-invite"
                  className="life-review-share-modal-tabs"
                  items={[
                    {
                      key: 'new-invite',
                      label: 'New Invite',
                      children: (
                        <div className="life-review-share-form">
                          <label className="life-review-share-field-label" htmlFor="life-review-share-recipient-name">
                            Recipient Name
                          </label>
                          <Input
                            id="life-review-share-recipient-name"
                            placeholder="Enter recipient name"
                            value={shareRecipientName}
                            onChange={(event) => setShareRecipientName(event.target.value)}
                            status={showShareRecipientNameError ? 'error' : undefined}
                            disabled={isCreatingShare}
                          />
                          {showShareRecipientNameError && <p className="life-review-share-field-error">Recipient Name is required.</p>}

                          <label className="life-review-share-field-label" htmlFor="life-review-share-recipient-email">
                            Recipient Email
                          </label>
                          <Input
                            id="life-review-share-recipient-email"
                            type="email"
                            placeholder="Enter recipient email"
                            value={shareRecipientEmail}
                            onChange={(event) => setShareRecipientEmail(event.target.value)}
                            status={showShareRecipientEmailError ? 'error' : undefined}
                            disabled={isCreatingShare}
                          />
                          {showShareRecipientEmailError && <p className="life-review-share-field-error">Enter a valid Recipient Email.</p>}

                          {shareSubmitFeedback && (
                            <Alert type={isCreateShareError ? 'error' : 'success'} showIcon message={shareSubmitFeedback} />
                          )}

                          <div className="life-review-share-modal-actions">
                            <Button
                              type="default"
                              onClick={closeShareModal}
                              disabled={isCreatingShare}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="primary"
                              onClick={() => void handleSendShareInvite()}
                              loading={isCreatingShare}
                              disabled={isCreatingShare}
                            >
                              Share Now
                            </Button>
                          </div>
                        </div>
                      )
                    },
                    {
                      key: 'active-invites',
                      label: 'Active Invites',
                      children: (
                        <div className="life-review-active-invites">
                          <h4>Active shared links</h4>

                          {activeInviteFeedback && (
                            <Alert type={activeInviteActionError ? 'error' : 'success'} showIcon message={activeInviteFeedback} />
                          )}

                          {isFetchingLifeReviewShares ? (
                            <div className="life-review-active-invites-loading">
                              <Spin size="small" />
                            </div>
                          ) : activeLifeReviewShares.length ? (
                            <div className="life-review-active-invites-list">
                              {activeLifeReviewShares.map((share) => (
                                <div className="life-review-active-invites-item" key={share.id}>
                                  <div className="life-review-active-invites-copy">
                                    <p className="life-review-active-invites-name">{share.recipientName || 'Recipient'}</p>
                                    <p className="life-review-active-invites-email">{share.recipientEmail || 'No email provided'}</p>
                                  </div>

                                  <div className="life-review-active-invites-actions">
                                    <Button
                                      type="default"
                                      size="small"
                                      disabled={isActiveInviteActionInProgress}
                                      onClick={() => void handleResendShareInvite(share.id)}
                                    >
                                      Resend
                                    </Button>
                                    <Button
                                      type="default"
                                      size="small"
                                      danger
                                      disabled={isActiveInviteActionInProgress}
                                      onClick={() => void handleDeleteShareInvite(share.id)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="life-review-share-empty-state">No active shared links yet.</p>
                          )}

                          <div className="life-review-share-modal-actions">
                            <Button type="default" onClick={closeShareModal} disabled={isActiveInviteActionInProgress}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )
                    }
                  ]}
                />
              </div>
            </Modal>
          </div>
        )}
      </ContainerComponent>

      <Footer />
    </div>
  );
}
